/**
 * API Keys Service
 * T026: Implement ApiKeyService for API key management
 */

import type { GatewayClient } from './client'
import type {
  ApiKeyResponse,
  ApiKeyCreatedResponse,
  CreateApiKeyRequest,
  UpdateApiKeyRequest,
  ApiKeysListResponse,
  MessageResponse,
} from './types'

// ============================================================================
// Types
// ============================================================================

export interface ApiKeyService {
  /**
   * List all API keys for a project
   */
  list(projectId: string): Promise<ApiKeysListResponse>

  /**
   * Create a new API key for a project
   */
  create(
    projectId: string,
    request: CreateApiKeyRequest
  ): Promise<ApiKeyCreatedResponse>

  /**
   * Get API key by ID
   */
  get(projectId: string, keyId: string): Promise<ApiKeyResponse>

  /**
   * Update an API key
   */
  update(
    projectId: string,
    keyId: string,
    request: UpdateApiKeyRequest
  ): Promise<ApiKeyResponse>

  /**
   * Revoke (delete) an API key
   */
  revoke(projectId: string, keyId: string): Promise<void>
}

// ============================================================================
// Implementation
// ============================================================================

/**
 * Create an ApiKeyService instance
 */
export function createApiKeyService(client: GatewayClient): ApiKeyService {
  return {
    async list(projectId: string): Promise<ApiKeysListResponse> {
      return client.get<ApiKeysListResponse>(
        `/v1/projects/${projectId}/api-keys`
      )
    },

    async create(
      projectId: string,
      request: CreateApiKeyRequest
    ): Promise<ApiKeyCreatedResponse> {
      return client.post<ApiKeyCreatedResponse>(
        `/v1/projects/${projectId}/api-keys`,
        request
      )
    },

    async get(projectId: string, keyId: string): Promise<ApiKeyResponse> {
      return client.get<ApiKeyResponse>(
        `/v1/projects/${projectId}/api-keys/${keyId}`
      )
    },

    async update(
      projectId: string,
      keyId: string,
      request: UpdateApiKeyRequest
    ): Promise<ApiKeyResponse> {
      return client.patch<ApiKeyResponse>(
        `/v1/projects/${projectId}/api-keys/${keyId}`,
        request
      )
    },

    async revoke(projectId: string, keyId: string): Promise<void> {
      await client.delete<MessageResponse>(
        `/v1/projects/${projectId}/api-keys/${keyId}`
      )
    },
  }
}
